import React, { useState, useEffect } from "react";
import { useFormikContext, FieldArray } from "formik";
import { Row, Col, Form, Button, Table, Modal } from "react-bootstrap";
import { FaTrashAlt, FaPlus } from "react-icons/fa";
import { Autocomplete, Box } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import EditIcon from "@mui/icons-material/Edit";
import DOMPurify from "dompurify";
import TextEditor from "components/TextEditor/TextEditor";
import { useDispatch } from "react-redux";
import { AppDispatch } from "index";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import AddButton from "Common/Components/Buttons/AddButton";
import { QuotationFormValues } from "../initialValues";
import { MecordColors } from "Constants/Color";
import {
  autocompleteDropdownStyles,
  DETAILS_BG,
  filterPopoverStyles,
  tableStyles,
} from "Constants/Style";
import AddProductModal from "../Components/AddProductModal";
import AddShippingAddressModal from "../Components/AddShippingAddressModal";
import { fetchModelByDropDown } from "slices/Masters/model/thunk";
import { fetchProductsDropDownByModel } from "slices/Masters/product/thunk";
import { fetchGstByDropDown } from "slices/Masters/gst/thunk";
import NoSelectedProducts from "assets/images/Order/NoSelectedProducts.png";
import { formatIndianAmount } from "helpers/formatIndianAmount";

// Simple UUID generation function
const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

interface ShippingAddressOption {
  shipping_address_id: string;
  label: string;
  address: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
  phone_number: string;
  phone_country?: string;
  company_id?: string;
  is_default: boolean;
  created_at?: string;
  updated_at?: string;
  fullAddress?: any; // Store the full original address object
}

interface ProductShippingAddress {
  shipping_address_id: string;
  address: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
  phone_number: string;
  is_default: boolean;
}

const ProductListSection: React.FC = () => {
  const { values, setFieldValue, setFieldTouched, errors, touched } = useFormikContext<QuotationFormValues>();
  const dispatch = useDispatch<AppDispatch>();

  // Helper function to round to 2 decimal places
  const roundToTwoDecimals = (num: number): number => Math.round(num * 100) / 100;

  // Watch for currency, exchange rate, and currency symbol changes to recalculate product values
  const currentCurrency = (values as any).currency || "INR";
  const currentExchangeRate = (values as any).exchange_rate || "1";
  const currentCurrencySymbol = (values as any).currency_symbol || "₹";

  useEffect(() => {
    if (values.products && values.products.length > 0) {
      const updatedProducts = values.products.map((product) => {
        const unitPrice = product.unit_price || 0;
        const quantity = product.quantity || 0;
        const discountValueInput = product.discount_value || 0;
        const discountType = product.discount_type || "currency";
        const gstPercent = values.company?.gst_type === "Compositional gst" || currentCurrency !== "INR"
          ? 0
          : product.gst_percentage || 0;

        // Calculate rate in currency
        // unit_price is in INR, exchange_rate is "rate in INR" (e.g., 83 means 1 USD = 83 INR)
        // To convert INR to foreign currency: divide by exchange rate
        const rateInCurrency = roundToTwoDecimals(
          currentCurrency === "INR" ? unitPrice : unitPrice / parseFloat(currentExchangeRate)
        );
        const grossValue = roundToTwoDecimals(rateInCurrency * quantity);

        let discountValue = 0;

        // Calculate discount based on type
        if (discountType === "percentage") {
          discountValue = roundToTwoDecimals((grossValue * discountValueInput) / 100);
        } else {
          // Currency discount
          discountValue = roundToTwoDecimals(Math.min(discountValueInput, grossValue));
        }

        const netValue = roundToTwoDecimals(grossValue - discountValue);
        const gstValue = roundToTwoDecimals((netValue * gstPercent) / 100);
        const total = roundToTwoDecimals(netValue + gstValue);

        return {
          ...product,
          rate_in_currency: rateInCurrency,
          gross_value: grossValue,
          // IMPORTANT: Preserve the raw discount_value (user input), never overwrite it
          // Only set calculated_discount_value (computed amount for display)
          discount_value: product.discount_value || 0, // Preserve raw value
          calculated_discount_value: discountValue, // Set computed amount
          net_value: netValue,
          gst_value: gstValue,
          total: total,
        };
      });

      // Check if any values actually changed to prevent infinite loops
      const hasChanges = updatedProducts.some((updated, index) => {
        const current = values.products[index];
        return (
          updated.rate_in_currency !== current.rate_in_currency ||
          updated.gross_value !== current.gross_value ||
          updated.calculated_discount_value !== current.calculated_discount_value ||
          updated.net_value !== current.net_value ||
          updated.gst_value !== current.gst_value ||
          updated.total !== current.total
        );
      });

      if (hasChanges) {
        setFieldValue("products", updatedProducts);
      }
    }
  }, [currentCurrency, currentExchangeRate, currentCurrencySymbol, values.products, setFieldValue]);

  // Effect to handle GST type changes - force GST to 0 for Compositional GST customers or non-INR currency
  useEffect(() => {
    const shouldForceZeroGst = values.company?.gst_type === "Compositional gst" || (values as any).currency !== "INR";

    if (values.products && values.products.length > 0 && shouldForceZeroGst) {
      const updatedProducts = values.products.map((product) => ({
        ...product,
        gst_percentage: 0,
        gst_value: 0,
        total: roundToTwoDecimals(product.net_value || product.gross_value || 0)
      }));

      setFieldValue("products", updatedProducts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.company?.gst_type, (values as any).currency, values.products.length, setFieldValue]);

  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [newProduct, setNewProduct] = useState({
    model: "",
    product: "",
    quantity: "",
    shipping_address: null as ProductShippingAddress | null,
  });

  // Local state for models and products
  const [models, setModels] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [modelLoading, setModelLoading] = useState(false);
  const [productLoading, setProductLoading] = useState(false);

  // Local state for GST options
  const [gstOptions, setGstOptions] = useState<any[]>([]);
  const [gstLoading, setGstLoading] = useState(false);

  // State for shipping address modal
  const [showShippingAddressModal, setShowShippingAddressModal] = useState(false);
  const [productIdForAddress, setProductIdForAddress] = useState<string | null>(null);

  // Track which products are showing dropdown (editing mode) vs plain text
  const [editingShippingAddress, setEditingShippingAddress] = useState<Set<string>>(new Set());

  // Product Description modal (info icon)
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [descriptionModalContent, setDescriptionModalContent] = useState("");
  const [descriptionModalProductName, setDescriptionModalProductName] = useState("");

  // Additional Details modal (pencil icon)
  const [showAdditionalDetailsModal, setShowAdditionalDetailsModal] = useState(false);
  const [additionalDetailsIndex, setAdditionalDetailsIndex] = useState<number | null>(null);
  const [additionalDetailsDraft, setAdditionalDetailsDraft] = useState("");

  // Reset new product form when company changes
  useEffect(() => {
    setNewProduct({
      model: "",
      product: "",
      quantity: "",
      shipping_address: null,
    });
    setProducts([]);
  }, [values.company?.company_id]);


  // Fetch models on component mount
  useEffect(() => {
    const fetchModels = async () => {
      try {
        setModelLoading(true);
        const result = await dispatch(fetchModelByDropDown()).unwrap();
        setModels(result.data || []);
      } catch (error) {
        console.error("Failed to fetch models:", error);
        setModels([]);
      } finally {
        setModelLoading(false);
      }
    };

    fetchModels();
  }, [dispatch]);

  // Fetch GST options on component mount
  useEffect(() => {
    const fetchGstOptions = async () => {
      try {
        setGstLoading(true);
        const result = await dispatch(fetchGstByDropDown()).unwrap();
        setGstOptions(result.data || []);
      } catch (error) {
        console.error("Failed to fetch GST options:", error);
        setGstOptions([]);
      } finally {
        setGstLoading(false);
      }
    };

    fetchGstOptions();
  }, [dispatch]);



  // Handle model selection and fetch products
  const handleModelChange = async (modelId: string) => {
    setNewProduct({ ...newProduct, model: modelId, product: "" });
    if (modelId) {
      try {
        setProductLoading(true);
        const result = await dispatch(
          fetchProductsDropDownByModel(modelId)
        ).unwrap();
        console.log("Products fetched for model:", modelId, result);
        setProducts(result.data || []);
      } catch (error) {
        console.error("Failed to fetch products for model:", modelId, error);
        setProducts([]);
      } finally {
        setProductLoading(false);
      }
    } else {
      setProducts([]);
    }
  };

  // Use models directly from API response
  const modelOptionsForAutocomplete = models || [];

  const productOptionsForAutocomplete = products || [];

  const handleAddProduct = () => {
    if (!newProduct.model || !newProduct.product || !newProduct.quantity) {
      return;
    }

    const selectedProduct = products.find(
      (p: any) => p.product_id === newProduct.product
    );
    const selectedModel = models.find(
      (m: any) => m.model_id === newProduct.model
    );

    if (selectedProduct && selectedModel) {
      const unitPrice = parseFloat(selectedProduct.unit_price || "0");
      const quantity = parseInt(newProduct.quantity);
      const gstPercentage = values.company?.gst_type === "Compositional gst"
        ? 0
        : parseFloat(selectedProduct.hsn?.gst?.gst_percentage || "0");

      // Get currency and exchange rate from parent form
      const currency = (values as any).currency || "INR";
      const exchangeRate = parseFloat((values as any).exchange_rate || "1");

      // Calculate currency conversions
      // unit_price is in INR, exchange_rate is "rate in INR" (e.g., 83 means 1 USD = 83 INR)
      // To convert INR to foreign currency: divide by exchange rate
      const rateInCurrency = roundToTwoDecimals(
        currency === "INR" ? unitPrice : unitPrice / exchangeRate
      );
      const grossValue = roundToTwoDecimals(rateInCurrency * quantity);

      // Validate that the selected product has a valid product_id
      if (!selectedProduct.product_id) {
        alert('Selected product does not have a valid ID. Please select a different product.');
        return;
      }

      // Additional validation for product_id format
      const isValidFormat = selectedProduct.product_id.length > 10 && !/^\d+$/.test(selectedProduct.product_id);
      if (!isValidFormat) {
        alert('Selected product has an invalid ID format. Please select a different product.');
        console.error('Invalid product_id format:', selectedProduct.product_id);
        return;
      }

      const product = {
        id: generateUUID(),
        sr_no: values.products.length + 1,
        // Backend mandatory fields
        product_id: selectedProduct.product_id,
        product_name: selectedProduct.product_name || selectedProduct.name,
        category_name: selectedProduct.category || selectedProduct.category_name || "",
        sub_category_name: selectedProduct.sub_category || selectedProduct.sub_category_name || "",
        product_type: selectedProduct.type || selectedProduct.product_type || [],
        make: selectedModel.make_name,
        make_name: selectedModel.make_name,
        model: selectedModel.model_name,
        model_name: selectedModel.model_name,
        unit: selectedProduct.unit?.unit_name || "Nos",
        unit_name: selectedProduct.unit?.unit_name || "Nos",
        unit_price: unitPrice,
        hsn_code: selectedProduct.hsn?.hsn_code || selectedProduct.hsn_code,
        gst_percentage: gstPercentage,
        gst_value: 0,
        remark: "",
        sku: selectedProduct.product_sku || selectedProduct.sku,
        product_sku: selectedProduct.product_sku || selectedProduct.sku,
        quantity: quantity,
        product_quantity: quantity,
        product_total: roundToTwoDecimals(grossValue),
        product_net_total: roundToTwoDecimals(grossValue),
        discount_type: "currency",
        discount_value: 0, // raw value expected by backend
        serviceable: true, // Default to true, user can toggle via UI
        is_serviceable: true, // Default to true, user can toggle via UI
        // Frontend specific fields
        calculated_discount_value: 0,
        net_value: grossValue,
        total: grossValue,
        shipping_address: newProduct.shipping_address || (null as ProductShippingAddress | null),
        rate_in_currency: rateInCurrency,
        gross_value: grossValue,
        prod_description: selectedProduct.prod_description ?? selectedProduct.description ?? "",
        additional_details: selectedProduct.additional_details ?? "",
      };

      setFieldValue("products", [...values.products, product]);
      setNewProduct({ model: "", product: "", quantity: "", shipping_address: null });
    }
  };

  // Get currency symbol from form values (set by CurrencyExchangeSection)
  const getCurrencySymbol = () => {
    return (values as any).currency_symbol || '₹';
  };

  // Get shipping address options from selected company
  const getShippingAddressOptions = (): ShippingAddressOption[] => {
    const company = (values as any).company;
    if (!company || !company.shipping_addresses) return [];

    return company.shipping_addresses.map((address: any): ShippingAddressOption => ({
      shipping_address_id: address.shipping_address_id,
      label: `${address.address}, ${address.city}, ${address.state} - ${address.pincode}`,
      address: address.address,
      city: address.city,
      state: address.state,
      country: address.country,
      pincode: address.pincode,
      phone_number: address.phone_number,
      phone_country: address.phone_country,
      company_id: address.company_id || company.company_id,
      is_default: address.is_default,
      created_at: address.created_at,
      updated_at: address.updated_at,
      // Store the full original address object for reference
      fullAddress: address
    } as ShippingAddressOption));
  };

  // Get selected shipping address display text
  const getSelectedShippingAddressText = (shippingAddress: ProductShippingAddress | string | null) => {
    if (!shippingAddress || typeof shippingAddress === 'string') return '';
    if (typeof shippingAddress === 'object' &&
      shippingAddress &&
      'address' in shippingAddress &&
      'city' in shippingAddress &&
      'state' in shippingAddress &&
      'pincode' in shippingAddress) {
      return `${shippingAddress.address}, ${shippingAddress.city}, ${shippingAddress.state} - ${shippingAddress.pincode}`;
    }
    return '';
  };


  const formatPrice = (value: string | number) => {
    return `${getCurrencySymbol()} ${formatIndianAmount(value)}`;
  };

  return (
    <div className="rounded">
      <style>
        {`
          /* Make React-Bootstrap white close button fully bright */
          .bright-white-close .btn-close {
            filter: invert(1) brightness(2); /* turns faded white to bright white */
            opacity: 1;                       /* ensure fully visible */
          }
          .bright-white-close .btn-close:hover {
            filter: invert(1) brightness(2.5); /* slightly brighter on hover */
          }
          .quotation-description-modal-body {
            max-height: 70vh;
            overflow-x: hidden;
            overflow-y: auto;
          }
          .quotation-description-content {
            max-width: 100%;
            overflow-x: hidden;
            overflow-wrap: anywhere;
            word-break: break-word;
          }
          .quotation-description-content * {
            box-sizing: border-box;
            max-width: 100%;
            overflow-wrap: anywhere;
            word-break: break-word;
            white-space: normal !important;
          }
          .quotation-description-content img,
          .quotation-description-content video,
          .quotation-description-content iframe {
            display: block;
            height: auto;
            max-width: 100%;
          }
          .quotation-description-content table {
            display: block;
            max-width: 100%;
            overflow-x: auto;
          }
          .quotation-description-content h1,
          .quotation-description-content h2,
          .quotation-description-content h3 {
            line-height: 1.2;
          }
        `}
      </style>
      <div className="d-flex justify-content-start align-items-center mb-3 gap-2">
        <h5 className="fw-semibold mb-1 me-2">Product</h5>
        <Button
          size="sm"
          variant="light"
          className="fw-semibold mb-1 rounded"
          onClick={() => setShowAddProductModal(true)}
          style={{
            backgroundColor: MecordColors?.LightGrayColor,
          }}
        >
          + Add New Product
        </Button>
      </div>

      {/* Product List Container */}
      <div
        className="p-3 rounded"
        style={{ backgroundColor: MecordColors?.LightThemeBGColor }}
      >
        {/* Add Product Form */}
        <Row className="g-3 mb-4">
          <Col xs={12} sm={6} md={4} lg={3}>
            <Form.Group>
              <Form.Label>Model <span style={{ color: "red" }}>*</span></Form.Label>
              <Autocomplete
                options={modelOptionsForAutocomplete}
                loading={modelLoading}
                value={
                  newProduct.model
                    ? modelOptionsForAutocomplete.find(
                      (option: any) => option.model_id === newProduct.model
                    ) || null
                    : null
                }
                getOptionLabel={(option: any) => option.model_name || ""}
                isOptionEqualToValue={(option: any, value: any) =>
                  option.model_id === value.model_id
                }
                noOptionsText={
                  modelLoading ? "Loading..." : "No models available"
                }
                onChange={(_: any, newValue: any) => {
                  handleModelChange(newValue?.model_id || "");
                }}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option.model_id} style={{ padding: 0 }}>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.8,
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: MecordColors.Hover,
                          color: MecordColors.ActiveText,
                        },
                        ...(selected && {
                          backgroundColor: MecordColors.ActiveBackground,
                          color: MecordColors.ActiveText,
                        }),
                      }}
                    >
                      <span>{option.model_name}</span>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <BootstrapTextField
                    {...params}
                    placeholder="Select Model"
                    size="small"
                    sx={autocompleteDropdownStyles.inputSx}
                  />
                )}
              />
            </Form.Group>
          </Col>

          <Col xs={12} sm={6} md={4} lg={3}>
            <Form.Group>
              <Form.Label>Product <span style={{ color: "red" }}>*</span></Form.Label>
              <Autocomplete
                key={newProduct.product || "empty"}
                options={productLoading ? [] : productOptionsForAutocomplete}
                loading={productLoading}
                value={
                  newProduct.product
                    ? productOptionsForAutocomplete.find(
                      (option: any) =>
                        option.product_id === newProduct.product
                    )
                    : null
                }
                getOptionLabel={(option: any) =>
                  option.product_name || option.name || ""
                }
                isOptionEqualToValue={(option: any, value: any) =>
                  option.product_id === value.product_id
                }
                noOptionsText={
                  productLoading ? "Loading..." : "No products available"
                }
                onChange={(_: any, newValue: any) => {
                  setNewProduct({
                    ...newProduct,
                    product: newValue?.product_id || "",
                  });
                }}
                disabled={!newProduct.model}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option.product_id} style={{ padding: 0 }}>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.8,
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: MecordColors.Hover,
                          color: MecordColors.ActiveText,
                        },
                        ...(selected && {
                          backgroundColor: MecordColors.ActiveBackground,
                          color: MecordColors.ActiveText,
                        }),
                      }}
                    >
                      <div style={{ width: "100%" }}>
                        <div
                          style={{ fontWeight: "bold", marginBottom: "4px" }}
                        >
                          {option.product_name || option.name}
                        </div>
                        <div
                          style={{
                            fontSize: "0.75rem",
                            color: "#666",
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <span>Make: {option.make?.make_name || "N/A"}</span>
                          <span>
                            Model: {option.model?.model_name || "N/A"}
                          </span>
                        </div>
                        <div
                          style={{
                            fontSize: "0.75rem",
                            color: "#666",
                            display: "flex",
                            justifyContent: "space-between",
                            marginTop: "2px",
                          }}
                        >
                          <span>
                            SKU: {option.product_sku || option.sku || "N/A"}
                          </span>
                          <span>
                            HSN:{" "}
                            {option.hsn?.hsn_code || option.hsn_code || "N/A"}
                          </span>
                          <span
                            style={{ fontWeight: "bold", color: "#007bff" }}
                          >
                            ₹{option.unit_price || "N/A"}
                          </span>
                        </div>
                      </div>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <BootstrapTextField
                    {...params}
                    placeholder="Select Product"
                    size="small"
                    sx={autocompleteDropdownStyles.inputSx}
                  />
                )}
              />
            </Form.Group>
          </Col>

          <Col xs={12} sm={6} md={4} lg={3}>
            <Form.Group>
              <Form.Label>Quantity <span style={{ color: "red" }}>*</span></Form.Label>
              <Form.Control
                type="number"
                placeholder="Enter Quantity"
                value={newProduct.quantity}
                onChange={(e) =>
                  setNewProduct({ ...newProduct, quantity: e.target.value })
                }
                min="0"
                step="1"
              />
            </Form.Group>
          </Col>

          <Col xs={12} sm={6} md={4} lg={3}>
            <Form.Group>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <Form.Label style={{ margin: 0 }}>Shipping Address <span style={{ color: "red" }}>*</span></Form.Label>
                <span
                  style={{
                    color: MecordColors.ThemeBGColor,
                    fontSize: "0.85rem",
                    cursor: "pointer",
                    fontWeight: "500"
                  }}
                  onClick={() => {
                    setProductIdForAddress(null);
                    setShowShippingAddressModal(true)
                  }}
                >
                  <i className="bi bi-plus-circle" style={{ marginRight: "4px" }}></i>
                  Add New
                </span>
              </div>

              <Autocomplete
                options={getShippingAddressOptions()}
                getOptionLabel={(option: ShippingAddressOption) => option.label || ""}
                isOptionEqualToValue={(option: ShippingAddressOption, value: ShippingAddressOption | null) =>
                  option.shipping_address_id === value?.shipping_address_id
                }
                value={
                  newProduct.shipping_address && typeof newProduct.shipping_address === 'object' && 'shipping_address_id' in newProduct.shipping_address && (newProduct.shipping_address as ProductShippingAddress).shipping_address_id
                    ? (() => {
                      const options = getShippingAddressOptions();
                      const found = options.find(
                        (option) => option.shipping_address_id === (newProduct.shipping_address as ProductShippingAddress).shipping_address_id
                      );
                      if (!found && newProduct.shipping_address) {
                        const addr = newProduct.shipping_address as ProductShippingAddress;
                        return {
                          shipping_address_id: addr.shipping_address_id,
                          label: `${addr.address}, ${addr.city}, ${addr.state} - ${addr.pincode}`,
                          address: addr.address,
                          city: addr.city,
                          state: addr.state,
                          country: addr.country,
                          pincode: addr.pincode,
                          phone_number: addr.phone_number,
                          is_default: addr.is_default
                        };
                      }
                      return found || null;
                    })()
                    : null
                }
                onChange={(_, newValue) => {
                  if (newValue) {
                    const company = (values as any).company;
                    const fullAddress = company?.shipping_addresses?.find(
                      (addr: any) => addr.shipping_address_id === newValue.shipping_address_id
                    ) || (newValue as any).fullAddress || {
                      shipping_address_id: newValue.shipping_address_id,
                      company_id: newValue.company_id || (values as any).company?.company_id,
                      address: newValue.address,
                      city: newValue.city,
                      state: newValue.state,
                      country: newValue.country,
                      pincode: newValue.pincode,
                      phone_number: newValue.phone_number,
                      phone_country: newValue.phone_country || "+91",
                      is_default: newValue.is_default || false,
                      created_at: newValue.created_at,
                      updated_at: newValue.updated_at
                    };

                    setNewProduct({ ...newProduct, shipping_address: fullAddress });
                  } else {
                    setNewProduct({ ...newProduct, shipping_address: null });
                  }
                }}
                size="small"
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option: ShippingAddressOption, { selected }) => (
                  <li {...props} key={option.shipping_address_id} style={{ padding: 0 }}>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.8,
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: MecordColors.Hover,
                          color: MecordColors.ActiveText,
                        },
                        ...(selected && {
                          backgroundColor: MecordColors.ActiveBackground,
                          color: MecordColors.ActiveText,
                        }),
                      }}
                    >
                      <div style={{ fontWeight: "bold", fontSize: "0.85rem" }}>
                        {option.address}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "#666" }}>
                        {option.city}, {option.state} - {option.pincode}
                      </div>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <BootstrapTextField
                    {...params}
                    placeholder="Select Shipping Address"
                    size="small"
                    sx={autocompleteDropdownStyles.inputSx}
                  />
                )}
              />
            </Form.Group>
          </Col>

          <Col xs={12} sm={12} md="auto" lg="auto" className="d-flex align-items-end ms-auto">
            <AddButton
              onClick={handleAddProduct}
              disabled={
                !newProduct.model || !newProduct.product || !newProduct.quantity || !newProduct.shipping_address
              }
              label="Add"
              height="38px"
              minWidth="80px"
              variant="danger"
            />
          </Col>
        </Row>

        {/* Products Table */}
        <FieldArray name="products">
          {({ push, remove }) => (
            <div>
              {values.products.length > 0 ? (
                <div
                  className="table-responsive bg-white rounded"
                  style={{ overflowX: "auto", maxWidth: "100%" }}
                >
                  <Table
                    hover
                    style={{
                      minWidth: "1550px",
                      tableLayout: "fixed",
                      width: "1550px",
                    }}
                  >
                    <thead>
                      <tr
                        style={{ backgroundColor: MecordColors.LightGrayColor }}
                      >
                        <th style={{ width: "60px", whiteSpace: "nowrap" }}>
                          Sr No.
                        </th>
                        <th style={{ width: "300px", whiteSpace: "nowrap" }}>
                          Products
                        </th>
                        <th style={{ width: "150px", whiteSpace: "nowrap", textAlign: "center" }}>
                          Additional Details
                        </th>
                        <th style={{ width: "120px", whiteSpace: "nowrap" }}>
                          Serviceable
                        </th>
                        <th style={{ width: "120px", whiteSpace: "nowrap" }}>
                          Product Quantity
                        </th>
                        <th style={{ width: "100px", whiteSpace: "nowrap" }}>
                          Unit
                        </th>
                        <th style={{ width: "120px", whiteSpace: "nowrap" }}>
                          Unit Price (INR)
                        </th>
                        <th style={{ width: "120px", whiteSpace: "nowrap" }}>
                          Rate in Currency
                        </th>
                        <th style={{ width: "120px", whiteSpace: "nowrap" }}>
                          Gross Value
                        </th>
                        <th style={{ width: "180px", whiteSpace: "nowrap" }}>
                          Discount/Flat
                        </th>
                        <th style={{ width: "110px", whiteSpace: "nowrap" }}>
                          Discount Value
                        </th>
                        <th style={{ width: "100px", whiteSpace: "nowrap" }}>
                          Net Value
                        </th>
                        <th style={{ width: "120px", whiteSpace: "nowrap" }}>
                          GST (%)
                        </th>
                        <th style={{ width: "100px", whiteSpace: "nowrap" }}>
                          GST Value
                        </th>
                        <th style={{ width: "100px", whiteSpace: "nowrap" }}>
                          Total
                        </th>
                        <th style={{ width: "300px", whiteSpace: "nowrap" }}>
                          Shipping Address
                        </th>
                        <th
                          style={{
                            position: "sticky",
                            right: 0,
                            backgroundColor: MecordColors.LightGrayColor,
                            borderLeft: `1px solid ${MecordColors.LightGrayColor}`,
                            zIndex: 10,
                            width: "80px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody style={{ backgroundColor: "transparent" }}>
                      {values.products.map((product, index) => {
                        // Safety check to prevent rendering invalid product objects
                        if (!product || typeof product !== 'object' || !product.id) {
                          return null;
                        }

                        const handleFieldChange = (
                          field: string,
                          value: any
                        ) => {
                          const updatedProducts = [...values.products];

                          // Helper function to round to 2 decimal places
                          const roundToTwoDecimals = (num: number): number => Math.round(num * 100) / 100;

                          // Round numeric input values to 2 decimal places
                          if (typeof value === 'number' && (field === 'unit_price' || field === 'discount_value' || field === 'gst_percentage' || field === 'quantity')) {
                            value = roundToTwoDecimals(value);
                          }

                          (updatedProducts[index] as any)[field] = value;

                          // Get currency and exchange rate from parent form
                          const currency = (values as any).currency || "INR";
                          const exchangeRate = parseFloat(
                            (values as any).exchange_rate || "1"
                          );

                          // Force GST percentage to 0 if customer has Compositional GST type or currency is not INR
                          if (field === "gst_percentage" && (values.company?.gst_type === "Compositional gst" || currency !== "INR")) {
                            (updatedProducts[index] as any)[field] = 0;
                          }

                          // Recalculate dependent values
                          const unitPrice =
                            updatedProducts[index].unit_price || 0;
                          const quantity = updatedProducts[index].quantity || 0;

                          // Calculate rate in currency (always in INR for unit_price, convert to selected currency)
                          // unit_price is in INR, exchange_rate is "rate in INR" (e.g., 83 means 1 USD = 83 INR)
                          // To convert INR to foreign currency: divide by exchange rate
                          const rateInCurrency = roundToTwoDecimals(
                            currency === "INR"
                              ? unitPrice
                              : unitPrice / exchangeRate
                          );
                          const grossValue = roundToTwoDecimals(rateInCurrency * quantity);

                          const discountValueInput =
                            updatedProducts[index].discount_value || 0;
                          const discountType =
                            updatedProducts[index].discount_type || "currency";
                          const gstPercent =
                            values.company?.gst_type === "Compositional gst" || currency !== "INR"
                              ? 0
                              : updatedProducts[index].gst_percentage || 0;

                          let discountValue = 0;

                          // Calculate discount based on type (apply to gross value)
                          if (discountType === "percentage") {
                            discountValue = roundToTwoDecimals(
                              (grossValue * discountValueInput) / 100
                            );
                          } else {
                            // Currency discount
                            discountValue = roundToTwoDecimals(
                              Math.min(discountValueInput, grossValue)
                            );
                          }

                          const netValue = roundToTwoDecimals(grossValue - discountValue);
                          const gstValue = roundToTwoDecimals((netValue * gstPercent) / 100);
                          const total = roundToTwoDecimals(netValue + gstValue);

                          updatedProducts[index].rate_in_currency =
                            rateInCurrency;
                          updatedProducts[index].gross_value = grossValue;
                          updatedProducts[index].calculated_discount_value = discountValue;
                          updatedProducts[index].net_value = netValue;
                          updatedProducts[index].gst_value = gstValue;
                          updatedProducts[index].total = total;

                          setFieldValue("products", updatedProducts);
                        };

                        return (
                          <tr key={product.id}>
                            <td style={{ width: "60px", textAlign: "center" }}>
                              {product.sr_no}
                            </td>
                            <td style={{ width: "300px", verticalAlign: "middle" }}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  position: "relative",
                                }}
                              >
                                <div>
                                  {product.product_name}
                                  <br />
                                  <small style={{ fontSize: "0.75rem" }}>
                                    SKU:{" "}
                                    <span className="text-muted">
                                      {product.sku}
                                    </span>{" "}
                                    HSN Code:{" "}
                                    <span className="text-muted">
                                      {product.hsn_code}
                                    </span>
                                  </small>
                                </div>
                                <InfoIcon
                                  style={{
                                    cursor: "pointer",
                                    color: tableStyles.primaryColor || MecordColors?.ThemeBGColor,
                                    fontSize: "18px",
                                    flexShrink: 0,
                                  }}
                                  onClick={() => {
                                    const content =
                                      (product as any).prod_description ??
                                      (product as any).description ??
                                      "";
                                    setDescriptionModalContent(content);
                                    setDescriptionModalProductName(product.product_name || "");
                                    setShowDescriptionModal(true);
                                  }}
                                />
                              </div>
                            </td>
                            <td style={{ width: "60px", textAlign: "center", verticalAlign: "middle" }}>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <EditIcon
                                  style={{
                                    cursor: "pointer",
                                    color: tableStyles.primaryColor || MecordColors?.ThemeBGColor,
                                    fontSize: "20px",
                                  }}
                                  onClick={() => {
                                    setAdditionalDetailsIndex(index);
                                    setAdditionalDetailsDraft(
                                      (product as any).additional_details ?? ""
                                    );
                                    setShowAdditionalDetailsModal(true);
                                  }}
                                />
                              </div>
                            </td>
                            <td style={{ width: "120px", textAlign: "center" }}>
                              <div
                                style={{
                                  fontSize: "1.5rem",
                                  cursor: "pointer",
                                  display: "inline-block",
                                }}
                                onClick={() => {
                                  const updatedProducts = [...values.products];
                                  updatedProducts[index].is_serviceable =
                                    !product.is_serviceable;
                                  setFieldValue("products", updatedProducts);
                                }}
                              >
                                <i
                                  className={`bi ${product.is_serviceable
                                    ? "bi-toggle-on text-success"
                                    : "bi-toggle-off text-danger"
                                    }`}
                                />
                              </div>
                            </td>
                            <td style={{ width: "120px" }}>
                              <Form.Control
                                type="number"
                                size="sm"
                                min={1}
                                step={1}
                                value={product.quantity ?? ""}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  const val = parseInt(raw, 10);

                                  if (raw === "") {
                                    handleFieldChange("quantity","");
                                  } else {
                                    handleFieldChange(
                                      "quantity",
                                      val
                                    );
                                  }
                                  setFieldTouched(`products.${index}.quantity`, true);
                                }}
                                isInvalid={!!(errors?.products?.[index] as any)?.quantity}
                                style={{ width: "100%" }}
                              />
                              <Form.Control.Feedback type="invalid">
                                {(errors?.products?.[index] as any)?.quantity}
                              </Form.Control.Feedback>
                            </td>
                            <td style={{ width: "100px" }}>{product.unit}</td>
                            <td style={{ width: "120px" }}>
                              <Form.Control
                                type="number"
                                size="sm"
                                value={product.unit_price.toString()}
                                onChange={(e) =>
                                  handleFieldChange(
                                    "unit_price",
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                min="0"
                                step="0.01"
                                style={{ width: "100%" }}
                              />
                            </td>
                            <td style={{ width: "120px" }}>
                              {formatPrice(product.rate_in_currency)}
                            </td>
                            <td style={{ width: "120px" }}>
                              {formatPrice(product.gross_value)}
                            </td>
                            <td style={{ width: "180px" }}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                <Form.Control
                                  type="number"
                                  size="sm"
                                  value={(product.discount_value ?? 0).toString()}
                                  onChange={(e) => {
                                    const value =
                                      parseFloat(e.target.value) || 0;
                                    const discountType =
                                      product.discount_type || "currency";
                                    const grossValue = product.gross_value || 0;

                                    // Validation based on discount type
                                    if (
                                      discountType === "percentage" &&
                                      value > 100
                                    )
                                      return;
                                    if (
                                      discountType === "currency" &&
                                      value > grossValue
                                    )
                                      return;

                                    handleFieldChange("discount_value", value);
                                  }}
                                  min="0"
                                  step="0.01"
                                  max={
                                    product.discount_type === "percentage"
                                      ? "100"
                                      : product.gross_value?.toString()
                                  }
                                  style={{ flex: 1 }}
                                />
                                <Form.Select
                                  size="sm"
                                  value={product.discount_type || "currency"}
                                  onChange={(e) => {
                                    const discountType = e.target.value as
                                      | "percentage"
                                      | "currency";
                                    // Reset discount value when changing type
                                    handleFieldChange("discount_value", 0);
                                    handleFieldChange(
                                      "discount_type",
                                      discountType
                                    );
                                  }}
                                  style={{ width: "50px", padding: "4px" }}
                                >
                                  <option value="currency">
                                    {getCurrencySymbol()}
                                  </option>
                                  <option value="percentage">%</option>
                                </Form.Select>
                              </div>
                            </td>
                            <td style={{ width: "110px" }}>
                              {formatPrice(product.calculated_discount_value || 0)}
                            </td>
                            <td style={{ width: "100px" }}>
                              {formatPrice(product.net_value)}
                            </td>
                            <td style={{ width: "120px" }}>
                              <Autocomplete
                                disableClearable
                                options={gstOptions}
                                getOptionLabel={(option: any) => option.gst_percentage?.toString() || ""}
                                isOptionEqualToValue={(option: any, value: any) =>
                                  option.gst_percentage === value.gst_percentage
                                }
                                value={
                                  gstOptions.find(
                                    (option: any) => option.gst_percentage?.toString() === product.gst_percentage?.toString()
                                  ) || null
                                }
                                onChange={(_, newValue) => {
                                  handleFieldChange(
                                    "gst_percentage",
                                    parseFloat(newValue?.gst_percentage || "0")
                                  );
                                }}
                                disabled={values.company?.gst_type === "Compositional gst" || (values as any).currency !== "INR"}
                                size="small"
                                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                                renderOption={(props, option: any, { selected }) => (
                                  <li {...props} key={option.gst_id} style={{ padding: 0 }}>
                                    <Box
                                      sx={{
                                        px: 1.5,
                                        py: 0.5,
                                        width: "100%",
                                        cursor: "pointer",
                                        "&:hover": {
                                          backgroundColor: MecordColors.Hover,
                                          color: MecordColors.ActiveText,
                                        },
                                        ...(selected && {
                                          backgroundColor: MecordColors.ActiveBackground,
                                          color: MecordColors.ActiveText,
                                        }),
                                      }}
                                    >
                                      {option.gst_percentage}%
                                    </Box>
                                  </li>
                                )}
                                renderInput={(params) => (
                                  <BootstrapTextField
                                    {...params}
                                    placeholder="GST %"
                                    size="small"
                                    sx={{
                                      ...autocompleteDropdownStyles.inputSx,
                                      '& .MuiInputBase-root': {
                                        minHeight: '31px',
                                        height: '31px',
                                        fontSize: '0.875rem',
                                        padding: '0 8px',
                                      },
                                      '& .MuiInputBase-input': {
                                        padding: '4px 0',
                                        fontSize: '0.875rem',
                                      },
                                    }}
                                  />
                                )}
                              />
                            </td>
                            <td style={{ width: "100px" }}>
                              {formatPrice(product.gst_value)}
                            </td>
                            <td style={{ width: "100px" }}>
                              {formatPrice(product.total)}
                            </td>
                            <td style={{
                              width: "300px",
                              backgroundColor: "transparent",
                              borderLeft: "none"
                            }}>
                              {product.shipping_address && typeof product.shipping_address === 'object' && 'address' in product.shipping_address && !editingShippingAddress.has(product.id) ? (
                                // Show shipping address as plain text when selected
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    cursor: "pointer",
                                    padding: "4px 0"
                                  }}
                                  onClick={() => {
                                    // Show dropdown again to edit
                                    setEditingShippingAddress(prev => {
                                      const newSet = new Set(prev);
                                      newSet.add(product.id);
                                      return newSet;
                                    });
                                  }}
                                  title="Click to change shipping address"
                                >
                                  {getSelectedShippingAddressText(product.shipping_address as ProductShippingAddress)}
                                </div>
                              ) : (
                                // Show dropdown with plus button for selection/editing
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                  <div style={{ flex: 1 }}>
                                    <Autocomplete
                                      options={getShippingAddressOptions()}
                                      getOptionLabel={(option: ShippingAddressOption) => option.label || ""}
                                      isOptionEqualToValue={(option: ShippingAddressOption, value: ShippingAddressOption | null) =>
                                        option.shipping_address_id === value?.shipping_address_id
                                      }
                                      value={
                                        product.shipping_address && typeof product.shipping_address === 'object' && 'shipping_address_id' in product.shipping_address && (product.shipping_address as ProductShippingAddress).shipping_address_id
                                          ? (() => {
                                            const options = getShippingAddressOptions();
                                            const found = options.find(
                                              (option) => option.shipping_address_id === (product.shipping_address as ProductShippingAddress).shipping_address_id
                                            );
                                            // If not found in options but we have the shipping address data, create a temporary option
                                            if (!found && product.shipping_address) {
                                              const addr = product.shipping_address as ProductShippingAddress;
                                              return {
                                                shipping_address_id: addr.shipping_address_id,
                                                label: `${addr.address}, ${addr.city}, ${addr.state} - ${addr.pincode}`,
                                                address: addr.address,
                                                city: addr.city,
                                                state: addr.state,
                                                country: addr.country,
                                                pincode: addr.pincode,
                                                phone_number: addr.phone_number,
                                                is_default: addr.is_default
                                              };
                                            }
                                            return found || null;
                                          })()
                                          : null
                                      }
                                      onChange={(_, newValue) => {
                                        if (newValue) {
                                          // Get the full address object from company's shipping_addresses
                                          const company = (values as any).company;
                                          const fullAddress = company?.shipping_addresses?.find(
                                            (addr: any) => addr.shipping_address_id === newValue.shipping_address_id
                                          ) || (newValue as any).fullAddress || {
                                            shipping_address_id: newValue.shipping_address_id,
                                            company_id: newValue.company_id || (values as any).company?.company_id,
                                            address: newValue.address,
                                            city: newValue.city,
                                            state: newValue.state,
                                            country: newValue.country,
                                            pincode: newValue.pincode,
                                            phone_number: newValue.phone_number,
                                            phone_country: newValue.phone_country || "+91",
                                            is_default: newValue.is_default || false,
                                            created_at: newValue.created_at,
                                            updated_at: newValue.updated_at
                                          };

                                          handleFieldChange("shipping_address", fullAddress);
                                        } else {
                                          handleFieldChange("shipping_address", null);
                                        }
                                        // Hide dropdown and show plain text after selection
                                        setEditingShippingAddress(prev => {
                                          const newSet = new Set(prev);
                                          newSet.delete(product.id);
                                          return newSet;
                                        });
                                      }}
                                      size="small"
                                      ListboxProps={autocompleteDropdownStyles.ListboxProps}
                                      renderOption={(props, option: ShippingAddressOption, { selected }) => (
                                        <li {...props} key={option.shipping_address_id} style={{ padding: 0 }}>
                                          <Box
                                            sx={{
                                              px: 1.5,
                                              py: 0.8,
                                              width: "100%",
                                              display: "flex",
                                              flexDirection: "column",
                                              cursor: "pointer",
                                              "&:hover": {
                                                backgroundColor: MecordColors.Hover,
                                                color: MecordColors.ActiveText,
                                              },
                                              ...(selected && {
                                                backgroundColor: MecordColors.ActiveBackground,
                                                color: MecordColors.ActiveText,
                                              }),
                                            }}
                                          >
                                            <div style={{ fontWeight: "bold", fontSize: "0.85rem" }}>
                                              {option.address}
                                            </div>
                                            <div style={{ fontSize: "0.75rem", color: "#666" }}>
                                              {option.city}, {option.state} - {option.pincode}
                                              {/* {option.is_default && (
                                                <span style={{ color: "#007bff", marginLeft: "4px" }}>
                                                  (Default)
                                                </span>
                                              )} */}
                                            </div>
                                          </Box>
                                        </li>
                                      )}
                                      renderInput={(params) => (
                                        <BootstrapTextField
                                          {...params}
                                          placeholder="Select Shipping Address *"
                                          size="small"
                                          sx={autocompleteDropdownStyles.inputSx}
                                        />
                                      )}
                                    />
                                    {(!product.shipping_address || typeof product.shipping_address !== 'object') && (
                                      <div style={{
                                        fontSize: "0.75rem",
                                        color: "#dc3545",
                                        marginTop: "2px",
                                        fontWeight: "500"
                                      }}>
                                        Required
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </td>
                            <td
                              style={{
                                position: "sticky",
                                right: 0,
                                backgroundColor: MecordColors.LightGrayColor,
                                borderLeft: `1px solid ${MecordColors.LightGrayColor}`,
                                zIndex: 10,
                                width: "80px",
                                textAlign: "center",
                                verticalAlign: "middle",
                              }}
                            >
                              <Button
                                variant="link"
                                className="text-danger p-0 d-flex align-items-center justify-content-center"
                                onClick={() => remove(index)}
                                style={{ width: "100%", height: "100%" }}
                              >
                                <FaTrashAlt />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="text-center text-muted py-4 border rounded p-3 bg-white">
                  <img
                    src={NoSelectedProducts}
                    alt="No products selected"
                    className="mb-3"
                    style={{ maxWidth: "180px", height: "auto" }}
                  />
                  <div>Search existing products to add to this list or add</div>
                  <div>new products to get started</div>
                  <Button
                    variant="light"
                    className="mt-2"
                    style={{
                      backgroundColor: MecordColors.LightThemeBGColor,
                      borderColor: MecordColors.LightThemeBGColor,
                      minWidth: "80px",
                      color: MecordColors.ThemeBGColor,
                    }}
                    onClick={() => setShowAddProductModal(true)}
                  >
                    {" "}
                    + Add New Product
                  </Button>
                </div>
              )}
            </div>
          )}
        </FieldArray>
        {errors.products && touched.products && (
          <div className="invalid-feedback d-block">
            {typeof errors.products === 'string' ? errors.products : 'Invalid product data'}
          </div>
        )}
      </div>

      {/* Add Product Modal */}
      <AddProductModal
        show={showAddProductModal}
        onHide={() => setShowAddProductModal(false)}
        onSave={(productData) => {
          // TODO: Implement save logic
          // In a real app, this would refresh the product options list
          console.log("Product data saved:", productData);
        }}
      />

      {/* Add Shipping Address Modal */}
      <AddShippingAddressModal
        show={showShippingAddressModal}
        onHide={() => {
          setShowShippingAddressModal(false);
          setProductIdForAddress(null);
        }}
        companyId={(values as any).company?.company_id}
        formikValues={values}
        setFieldValue={setFieldValue}
        onAddressAdded={(newAddress) => {
          // Auto-select the newly added address
          if (newAddress) {
            // Store the full address object with all fields
            const fullAddress = {
              shipping_address_id: newAddress.shipping_address_id || newAddress.id,
              company_id: newAddress.company_id || (values as any).company?.company_id,
              address: newAddress.address,
              city: newAddress.city,
              state: newAddress.state,
              country: newAddress.country,
              pincode: newAddress.pincode || newAddress.pin_code,
              phone_number: newAddress.phone_number,
              phone_country: newAddress.phone_country || "+91",
              is_default: newAddress.is_default || false,
              created_at: newAddress.created_at,
              updated_at: newAddress.updated_at
            };

            if (productIdForAddress) {
              // Adding from table cell - update existing product
              const updatedProducts = values.products.map((p) => {
                if (p.id === productIdForAddress) {
                  return {
                    ...p,
                    shipping_address: fullAddress
                  };
                }
                return p;
              });
              setFieldValue("products", updatedProducts);

              // Clear editing state to show plain text after auto-selection
              setEditingShippingAddress(prev => {
                const newSet = new Set(prev);
                newSet.delete(productIdForAddress);
                return newSet;
              });
            } else {
              // Adding from product form - set in newProduct state
              setNewProduct(prev => ({
                ...prev,
                shipping_address: fullAddress
              }));
            }
          }
        }}
      />

      {/* Product Description Modal (Info icon) */}
      <Modal
        show={showDescriptionModal}
        onHide={() => {
          setShowDescriptionModal(false);
          setDescriptionModalContent("");
          setDescriptionModalProductName("");
        }}
        centered
        className="modal-lg"
      >
        <Modal.Header
          closeButton
          style={{
            fontFamily: tableStyles.header.fontFamily,
            fontSize: tableStyles.header.fontSize,
            borderBottom: "1px solid #dee2e6",
          }}
        >
          <Modal.Title
            style={{
              fontFamily: tableStyles.header.fontFamily,
              fontSize: tableStyles.header.fontSize,
              fontWeight: tableStyles.header.fontWeight,
              // color: tableStyles.header.color,
            }}
          >
            Product Description
            {descriptionModalProductName ? ` - ${descriptionModalProductName}` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          className="quotation-description-modal-body"
          style={{
            fontFamily: filterPopoverStyles.fontFamily,
            fontSize: filterPopoverStyles.fontSize,
          }}
        >
          {descriptionModalContent ? (
            <div
              className={`${DETAILS_BG} quotation-description-content`}
              style={{
                minHeight: "80px",
                fontFamily: filterPopoverStyles.fontFamily,
                fontSize: filterPopoverStyles.fontSize,
                width: "100%",
                display: "block",
              }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(descriptionModalContent),
              }}
            />
          ) : (
            <div
              className="text-muted"
              style={{
                fontFamily: filterPopoverStyles.fontFamily,
                fontSize: filterPopoverStyles.fontSize,
              }}
            >
              No description available.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-center">
          <Button
            variant="secondary"
            className="rounded"
            style={{
              fontFamily: filterPopoverStyles.button.fontFamily,
              fontSize: filterPopoverStyles.button.fontSize,
              borderColor: filterPopoverStyles.cancelButton.borderColor,
              color: filterPopoverStyles.cancelButton.color,
              backgroundColor: "#fff",
            }}
            onClick={() => {
              setShowDescriptionModal(false);
              setDescriptionModalContent("");
              setDescriptionModalProductName("");
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Additional Details Modal (Pencil icon) */}
      <Modal
        show={showAdditionalDetailsModal}
        onHide={() => {
          setShowAdditionalDetailsModal(false);
          setAdditionalDetailsIndex(null);
          setAdditionalDetailsDraft("");
        }}
        centered
        className="modal-lg"
      >
        <Modal.Header
          closeButton
          style={{
            fontFamily: tableStyles.header.fontFamily,
            fontSize: tableStyles.header.fontSize,
            borderBottom: "1px solid #dee2e6",
          }}
        >
          <Modal.Title
            style={{
              fontFamily: tableStyles.header.fontFamily,
              fontSize: tableStyles.header.fontSize,
              fontWeight: tableStyles.header.fontWeight,
              // color: tableStyles.header.color,
            }}
          >
            Additional Details
      
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            fontFamily: filterPopoverStyles.fontFamily,
            fontSize: filterPopoverStyles.fontSize,
          }}
        >
          <Form.Group className="mb-0">
   
            <TextEditor
              value={additionalDetailsDraft}
              onChange={setAdditionalDetailsDraft}
              placeholder="Enter additional details..."
              minHeightPx={120}
              maxHeightPx={200}
              blockImages={true}
            />
          </Form.Group>
          <div
            className="mb-0 mt-2"
        
          >
            <hr className="mb-0 w-100" />
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-center">
          <Button
            variant="secondary"
            className="px-3 rounded"
            style={{
              fontFamily: filterPopoverStyles.button.fontFamily,
              fontSize: filterPopoverStyles.button.fontSize,
              borderColor: filterPopoverStyles.cancelButton.borderColor,
              color: filterPopoverStyles.cancelButton.color,
              backgroundColor: "#fff",
            }}
            onClick={() => {
              setShowAdditionalDetailsModal(false);
              setAdditionalDetailsIndex(null);
              setAdditionalDetailsDraft("");
            }}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            className="px-3 rounded"
            style={{
              fontFamily: filterPopoverStyles.button.fontFamily,
              fontSize: filterPopoverStyles.button.fontSize,
              backgroundColor: filterPopoverStyles.applyButton.backgroundColor,
              borderColor: filterPopoverStyles.applyButton.backgroundColor,
              color: "#fff",
            }}
            onClick={() => {
              if (additionalDetailsIndex !== null) {
                const updatedProducts = [...values.products];
                (updatedProducts[additionalDetailsIndex] as any).additional_details =
                  additionalDetailsDraft;
                setFieldValue("products", updatedProducts);
              }
              setShowAdditionalDetailsModal(false);
              setAdditionalDetailsIndex(null);
              setAdditionalDetailsDraft("");
            }}
          >
            Update
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProductListSection;
